import { useCallback, useRef, useState } from 'react';
import {
  buyUnits,
  previewUnits,
  startUnitCardSetup,
  UnitsPreview,
} from '../../api/units-client';
import { compedRefusal } from '../../api/plan-client';
import type {
  StripeCardConfirmation,
  StripeIntent,
  StripeSummaryLine,
} from './stripe-card-modal';

/* ───────────────────────────────────────────────────────────────────────────
 * BUYING UNITS — one implementation, three screens (WP-22)
 *
 * There are two doors into a unit purchase — the billing page's "Buy more
 * units" row, and the out-of-units dialog that interrupts an allocation — and
 * both used to POST straight at /subscriptions/units with no quote and no
 * confirmation. Duplicating the money logic across them is how the two screens
 * end up quoting different numbers for the same change, so it lives here once
 * and each screen only renders it.
 *
 * THE ORDER IS THE PRODUCT, and it is the same on both doors:
 *
 *   1. QUOTE     ask the server what this costs. Every figure comes off that
 *                response; nothing here multiplies a rate by a count (D-12).
 *   2. CONFIRM   show the figures as SEPARATE labelled lines and wait for the
 *                user. A trialing org is charged $0.00 today while its monthly
 *                bill changes — one number cannot say both things.
 *   3. CARD      only when the server says a card is required, and only when it
 *                also says what adding one would do (`recovery`). Lobbero never
 *                holds the card; the modal confirms a Stripe SetupIntent and
 *                hands back an id the SERVER re-verifies.
 *   4. COMMIT    POST the new TOTAL. The allowance rises only after Stripe has
 *                confirmed, server-side.
 *
 * WHAT THIS HOOK DELIBERATELY DOES NOT DO: decide anything about money. It has
 * no rate, no floor, no minimum and no arithmetic beyond formatting cents. If a
 * figure is not in the server's response it is not shown.
 * ─────────────────────────────────────────────────────────────────────────── */

const symbolFor = (currency?: string) =>
  (currency || 'usd').toLowerCase() === 'usd' ? '$' : `${currency} `;

// Two decimals, always. Rounding $56.25 to "$56" is a smaller version of the
// same bug as quoting a floor nobody is billed.
export const money = (cents: number, currency?: string) =>
  `${symbolFor(currency)}${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface UnitPurchaseResult {
  units_allowed: number;
  units_used: number;
  units_remaining: number;
  subscription_created?: boolean;
  // What Stripe captured at the instant of the purchase (an annual increase).
  charged_now_cents?: number;
}

// The per-unit suffix for an interval: "mo" or "yr". (`interval.slice(0, 2)`
// used to produce "/ye" for an annual plan.)
export const perSuffix = (interval?: string | null) =>
  interval === 'year' ? 'yr' : 'mo';

// Stripe's hosted page for a payment that did not go through (a declined card
// or 3-D Secure) — carried on a PAYMENT_REQUIRED / PAYMENT_PENDING refusal.
// Paying there applies the change; nothing was changed until then.
export const paymentLinkOf = (err: unknown): string | null => {
  const e = err as {
    response?: { data?: { payment?: { hosted_invoice_url?: string | null } } };
  };

  return (
    (e &&
      e.response &&
      e.response.data &&
      e.response.data.payment &&
      e.response.data.payment.hosted_invoice_url) ||
    null
  );
};

export interface UseUnitPurchase {
  /** The server's quote, or null before one has arrived. */
  preview: UnitsPreview | null;
  /** True while the quote is in flight. */
  quoting: boolean;
  /** The absolute new total this purchase is for. */
  target: number | null;
  /**
   * The server's own refusal, or the reason the quote itself failed. Empty
   * string when there is nothing wrong.
   */
  refusal: string;
  /**
   * True only when the server asked for a card AND told us what adding one
   * would do. A refusal without `recovery` is not recoverable from this screen
   * — which is also what an older server sends — so the card form stays shut.
   */
  collectCard: boolean;
  /** True when the purchase cannot proceed at all. */
  blocked: boolean;
  /**
   * Stripe's page for a payment that did not go through — set when the server
   * answers PAYMENT_REQUIRED / PAYMENT_PENDING. Null otherwise.
   */
  paymentLink: string | null;
  summaryRows: StripeSummaryLine[];
  /** Ask the server to price a new TOTAL. */
  quote: (newTotal: number) => Promise<void>;
  /** Clear everything, e.g. when the dialog closes. */
  reset: () => void;
  /** Passed to the card modal. Runs at most once per modal, by contract. */
  prepareIntent: () => Promise<StripeIntent>;
  /** The card path's callback: commits using the confirmation as proof. */
  confirmed: (
    result: StripeCardConfirmation
  ) => Promise<UnitPurchaseResult>;
  /** The no-card path's callback: commits with no proof. */
  submit: () => Promise<UnitPurchaseResult>;
  /**
   * The card surface's `onError`. True for a refusal this hook has already put
   * on screen (through `refusal`), so the surface does not say it a second time.
   */
  handleCommitError: (err: unknown) => boolean;
}

// A complimentary account's refusal is named for what it is before anything
// else is read (COMPED_ACCOUNT, api/plan-client.ts).
const serverMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { message?: string } } };

  return (
    compedRefusal(err) ||
    (e && e.response && e.response.data && e.response.data.message) ||
    fallback
  );
};

// Read by shape, not through axios.isAxiosError: the error may come from any
// client instance, and the tests' axios mocks do not carry the helper.
const refusalCode = (err: unknown): string | null => {
  const e = err as { response?: { status?: number; data?: { code?: string } } };

  return (e && e.response && e.response.data && e.response.data.code) || null;
};

// The org's unit count moved between the quote and the confirm — another tab,
// another admin. The server changed nothing; the quote on screen is for a
// purchase that no longer exists.
const isQuantityChanged = (err: unknown): boolean =>
  refusalCode(err) === 'QUANTITY_CHANGED';

export const useUnitPurchase = (): UseUnitPurchase => {
  const [preview, setPreview] = useState<UnitsPreview | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [target, setTarget] = useState<number | null>(null);
  const [quoteError, setQuoteError] = useState('');
  const [commitPaymentLink, setCommitPaymentLink] = useState<string | null>(
    null
  );
  // The server's QUANTITY_CHANGED message. Held apart from `quoteError` because
  // it must SURVIVE the re-quote it triggers: the fresh figures arrive under it,
  // and the reader needs to know why the numbers just moved.
  const [changedNotice, setChangedNotice] = useState('');
  // The id the server minted. Kept as a fallback for the id Stripe reports back,
  // so a confirmation that resolves without one still carries a provable proof.
  const setupIntentIdRef = useRef<string | null>(null);
  // The target is read inside async callbacks that the modal owns; a ref keeps
  // them from closing over a stale render.
  const targetRef = useRef<number | null>(null);
  // The quote the user is looking at, for the same reason. The commit sends its
  // `current` and `proration_date` back, so the server can refuse a purchase
  // priced against a quantity that has since moved.
  const previewRef = useRef<UnitsPreview | null>(null);

  const showPreview = useCallback((next: UnitsPreview | null) => {
    previewRef.current = next;
    setPreview(next);
  }, []);

  const reset = useCallback(() => {
    showPreview(null);
    setQuoteError('');
    setChangedNotice('');
    setCommitPaymentLink(null);
    setTarget(null);
    targetRef.current = null;
    setupIntentIdRef.current = null;
  }, [showPreview]);

  const quote = useCallback(async (newTotal: number) => {
    showPreview(null);
    setQuoteError('');
    setChangedNotice('');
    setCommitPaymentLink(null);
    setTarget(newTotal);
    targetRef.current = newTotal;
    setupIntentIdRef.current = null;
    setQuoting(true);
    try {
      showPreview(await previewUnits(newTotal));
    } catch (err) {
      // No quote means no confirmation. Say so rather than offering a button
      // that would commit an amount nobody was shown.
      setQuoteError(
        serverMessage(err, 'Could not price this change. Please try again.')
      );
    } finally {
      setQuoting(false);
    }
  }, [showPreview]);

  // Re-price the SAME total after a QUANTITY_CHANGED refusal. Unlike `quote` it
  // keeps the old figures on screen until the new ones land (the confirm button
  // is disabled while `quoting`), and it keeps the SetupIntent id — a card the
  // user has just confirmed is still theirs to spend on the fresh quote.
  const requote = useCallback(async () => {
    const total = targetRef.current;
    if (total == null) return;

    setQuoting(true);
    try {
      showPreview(await previewUnits(total));
    } catch (err) {
      showPreview(null);
      setQuoteError(
        serverMessage(err, 'Could not price this change. Please try again.')
      );
    } finally {
      setQuoting(false);
    }
  }, [showPreview]);

  const prepareIntent = useCallback(async (): Promise<StripeIntent> => {
    const setup = await startUnitCardSetup();
    setupIntentIdRef.current = setup.setup_intent_id || null;

    // 'setup' is not decoration. The modal calls confirmCardSetup or
    // confirmCardPayment on the strength of it, and a SetupIntent confirmed as
    // a payment would report money moving when none did.
    return { mode: 'setup', clientSecret: setup.client_secret };
  }, []);

  const commit = useCallback(
    async (proof: string | null): Promise<UnitPurchaseResult> => {
      const total = targetRef.current;
      if (total == null) {
        throw new Error('Nothing to buy — no quantity was quoted.');
      }

      const shown = previewRef.current;
      setChangedNotice('');
      try {
        return await buyUnits(total, proof, {
          expected_current: shown ? shown.current : null,
          proration_date: shown ? shown.proration_date : null,
        });
      } catch (err) {
        // A declined or 3-D Secure charge: nothing was changed, and the page
        // that completes it is offered beside the server's message.
        setCommitPaymentLink(paymentLinkOf(err));
        // The quote went stale under the user. Nothing was charged; say so in
        // the server's words and put a fresh quote in front of them rather than
        // letting a retry commit figures nobody has seen.
        if (isQuantityChanged(err)) {
          setChangedNotice(
            serverMessage(
              err,
              'Your unit count changed since this price was shown. Check the new figures and confirm again.'
            )
          );
          void requote();
        }
        throw err;
      }
    },
    [requote]
  );

  const confirmed = useCallback(
    async (result: StripeCardConfirmation) => {
      // A PaymentIntent here would mean money moved on a flow that is supposed
      // to SAVE a card and let the subscription bill it. The server would refuse
      // the proof anyway; failing loudly here says which side is wrong.
      if (result.confirmed !== 'confirmCardSetup') {
        throw new Error(
          'Unexpected payment confirmation while adding a card. Nothing was ' +
            'purchased — please contact support.'
        );
      }

      return commit(result.intentId || setupIntentIdRef.current);
    },
    [commit]
  );

  const submit = useCallback(() => commit(null), [commit]);

  const handleCommitError = useCallback(
    (err: unknown) => isQuantityChanged(err),
    []
  );

  const recovery = (preview && preview.recovery) || null;
  const refusing = !!preview && preview.mode === 'refuse';
  const collectCard = !!(
    refusing &&
    preview &&
    preview.requires_card &&
    recovery &&
    recovery.available
  );
  // A refusal of the FRESH quote outranks the note about why it was re-priced.
  const refusal =
    quoteError ||
    (refusing
      ? (preview && preview.message) || 'This change cannot be made right now.'
      : '') ||
    changedNotice;
  const blocked = !preview || (refusing && !collectCard);

  const summaryRows: StripeSummaryLine[] = [];
  if (preview) {
    summaryRows.push({
      label: 'Units',
      value: `${preview.current} → ${preview.target}`,
    });
    summaryRows.push({
      label: 'Rate',
      value: `${money(preview.rate_cents, preview.currency)}/unit/${preview.interval}`,
    });
    const chargeNow = preview.mode === 'charge_now';
    if (collectCard && recovery) {
      // The card path has its own "today" figure, from the plan rather than
      // from a Stripe preview — Stripe refuses to preview an upcoming invoice
      // for a card-less trial at all, which is exactly this org.
      summaryRows.push({
        label: 'Charged today',
        value: money(recovery.first_charge_cents, preview.currency),
      });
    } else {
      // An ANNUAL increase is invoiced and paid on confirm, so today's figure
      // is a charge, and it is exactly the invoice Stripe previewed.
      summaryRows.push({
        label: chargeNow ? 'Charged today' : 'Due today',
        value:
          preview.amount_due_now_cents == null
            ? 'Not available'
            : money(preview.amount_due_now_cents, preview.currency),
      });
    }
    // Earlier changes' adjustments are NOT in today's charge — Stripe settles
    // them on the renewal — so they are named apart rather than hidden.
    if (chargeNow && preview.pending_adjustments_cents) {
      summaryRows.push({
        label: 'Earlier adjustments, at renewal',
        // A credit reads as a credit — `money` printed "$-45.00".
        value:
          preview.pending_adjustments_cents < 0
            ? `${money(-preview.pending_adjustments_cents, preview.currency)} credit`
            : money(preview.pending_adjustments_cents, preview.currency),
      });
    }
    // On a charge-now change the proration IS today's charge; listing it again
    // as "on your next invoice" would say it is paid twice.
    if (
      !chargeNow &&
      preview.proration_cents != null &&
      preview.proration_cents !== 0
    ) {
      summaryRows.push({
        label: 'On your next invoice',
        value: money(preview.proration_cents, preview.currency),
      });
    }
    summaryRows.push({
      label: `New total per ${preview.interval}`,
      value: `${money(
        preview.new_recurring_total_cents,
        preview.currency
      )}/${perSuffix(preview.interval)}`,
      total: true,
    });
  }

  return {
    preview,
    quoting,
    target,
    refusal,
    collectCard,
    blocked,
    paymentLink:
      commitPaymentLink ||
      (preview && preview.payment && preview.payment.hosted_invoice_url) ||
      null,
    summaryRows,
    quote,
    reset,
    prepareIntent,
    confirmed,
    submit,
    handleCommitError,
  };
};
